<template>
  <div class="tn-booking">
    <template v-if="loading">
      <v-skeleton-loader type="card-heading, list-item-two-line" class="mb-6"></v-skeleton-loader>
      <div class="tn-booking__grid">
        <v-skeleton-loader type="card, card"></v-skeleton-loader>
        <v-skeleton-loader type="card"></v-skeleton-loader>
      </div>
    </template>

    <v-card v-else-if="!booking" class="tn-empty py-12">
      <v-icon size="44">mdi-file-alert-outline</v-icon>
      <div>Couldn't get the booking details.</div>
      <v-btn text color="primary" :to="{ name: 'hotelsBookings' }">Back to hotel bookings</v-btn>
    </v-card>

    <template v-else>
      <v-alert v-if="!booking.hotel_details" type="info" text dense class="mb-5">
        The hotel name and the customer's account show up once the latest API update is deployed.
      </v-alert>

      <booking-summary
        :booking="booking"
        type="hotels"
        icon="mdi-office-building-outline"
        :title="details.hotel.name || (booking.hotel_details ? 'Hotel name was not saved for this booking' : 'Hotel name not available yet')"
        :tags="[details.supplier]"
        :total="booking.total_price"
      >
        <template v-slot:actions>
          <v-btn
            v-if="canCancel"
            outlined
            color="error"
            small
            class="mt-2"
            :loading="btnLoad"
            :disabled="disableButtonEmail"
            @click="confirmCancel"
          >
            <v-icon left size="18">mdi-close-circle-outline</v-icon>
            Cancel reservation
          </v-btn>
        </template>
      </booking-summary>

      <div class="tn-booking__grid">
        <div class="tn-booking__col">
          <!-- Hotel & stay -->
          <v-card class="tn-panel">
            <div class="tn-panel__head"><v-icon size="20">mdi-bed-outline</v-icon> Hotel & stay</div>
            <div class="tn-panel__body">
              <stay-strip :start="details.stay.check_in" :end="details.stay.check_out" :middle="nightsLabel" />

              <dl class="tn-facts">
                <div><dt>Hotel</dt><dd>{{ details.hotel.name || '—' }}</dd></div>
                <div><dt>Address</dt><dd>{{ details.hotel.address || '—' }}</dd></div>
                <div><dt>Room</dt><dd>{{ details.hotel.room_type || '—' }}</dd></div>
                <div><dt>Board</dt><dd>{{ boardLabel(details.hotel.board_type) }}</dd></div>
                <div><dt>Guests</dt><dd>{{ guestsLabel }}</dd></div>
                <div><dt>Hotel price (before fees)</dt><dd>{{ details.base_price !== null && details.base_price !== undefined ? money(details.base_price) : '—' }}</dd></div>
              </dl>
            </div>
          </v-card>

          <!-- Cancellation policy -->
          <v-card class="tn-panel">
            <div class="tn-panel__head">
              <v-icon size="20">mdi-calendar-remove-outline</v-icon> Cancellation policy
              <v-spacer></v-spacer>
              <v-chip
                v-if="details.cancel_policy && details.cancel_policy.refundable !== null"
                small
                label
                class="tn-status-chip"
                :class="details.cancel_policy.refundable ? 'tn-status-chip--success' : 'tn-status-chip--error'"
              >
                {{ details.cancel_policy.refundable ? 'Refundable' : 'Non-refundable' }}
              </v-chip>
            </div>
            <div class="tn-panel__body">
              <ul v-if="details.cancel_policy && details.cancel_policy.penalties.length" class="tn-policy">
                <li v-for="(penalty, i) in details.cancel_policy.penalties" :key="i" :class="penalty.value ? 'is-fee' : 'is-free'">
                  <span class="tn-policy__dot"></span>
                  <div>
                    <div class="tn-policy__title">
                      <template v-if="!penalty.value">Free cancellation until {{ dateTime(penalty.deadline) }}</template>
                      <template v-else>From {{ dateTime(penalty.deadline) }}: {{ money(penalty.value, penalty.currency || 'USD') }} fee</template>
                    </div>
                  </div>
                </li>
              </ul>
              <p v-else class="tn-muted mb-0">No cancellation policy was saved with this booking.</p>
            </div>
          </v-card>

          <travellers-panel v-if="travellers.length" :travellers="travellers" />
        </div>

        <div class="tn-booking__col">
          <customer-panel
            :customer="details.customer || customer"
            :contact="contact"
            :lead-guest="details.lead_guest"
            :empty-text="booking.hotel_details ? 'No customer account is linked to this booking.' : 'Available after the API update.'"
          />

          <payment-panel :payment="payment" :amount="booking.total_price" />

          <references-panel :items="references" />

          <!-- Refund -->
          <v-card v-if="details.refund" class="tn-panel">
            <div class="tn-panel__head">
              <v-icon size="20">mdi-cash-refund</v-icon> Refund
              <v-spacer></v-spacer>
              <v-chip small label class="tn-status-chip" :class="details.refund.is_transferred ? 'tn-status-chip--success' : 'tn-status-chip--warning'">
                {{ details.refund.is_transferred ? 'Transferred' : 'To transfer' }}
              </v-chip>
            </div>
            <div class="tn-panel__body">
              <dl class="tn-facts tn-facts--stack">
                <div><dt>Refund amount</dt><dd>{{ money(details.refund.refund_amount) }}</dd></div>
                <div><dt>Total paid</dt><dd>{{ money(details.refund.total_paid) }}</dd></div>
                <div v-if="details.refund.transferred_at"><dt>Transferred at</dt><dd>{{ dateTime(details.refund.transferred_at) }}</dd></div>
              </dl>
              <v-btn text small color="primary" class="px-0 mt-2" :to="{ name: 'hotelRefunds' }">Open refunds <v-icon right size="16">mdi-arrow-right</v-icon></v-btn>
            </div>
          </v-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import BookingSummary from '../../components/booking/BookingSummary.vue'
import CustomerPanel from '../../components/booking/CustomerPanel.vue'
import PaymentPanel from '../../components/booking/PaymentPanel.vue'
import ReferencesPanel from '../../components/booking/ReferencesPanel.vue'
import StayStrip from '../../components/booking/StayStrip.vue'
import TravellersPanel from '../../components/booking/TravellersPanel.vue'
import bookingPage from '../../mixins/bookingPage'
import { headers, cancelReservation } from '../../links'

const GTA_TYPE = 'App\\Models\\GtaHotelPortfolio'

const boards = { RO: 'Room only', BB: 'Bed & breakfast', HB: 'Half board', FB: 'Full board', AI: 'All inclusive', SC: 'Self catering' }

function parsePolicy (policy) {
  if (typeof policy === 'string') {
    try {
      policy = JSON.parse(policy)
    } catch (e) {
      return null
    }
  }
  if (!policy || typeof policy !== 'object') return null
  const penalties = (policy.cancelPenalties || [])
    .map(p => ({ deadline: p.deadline, value: Number(p.value), currency: p.currency }))
    .sort((a, b) => String(a.deadline).localeCompare(String(b.deadline)))
  return { refundable: typeof policy.refundable === 'boolean' ? policy.refundable : null, penalties }
}

// What an API without `hotel_details` (not deployed yet) already returns.
function fallbackDetails (b) {
  const stay = (b.booking_hotels || [])[0] || {}
  const checkIn = stay.start_date || b.start_date || b.hotel_start_date
  const checkOut = stay.end_date || b.end_date || b.hotel_end_date
  const nights = checkIn && checkOut ? Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000) : null
  return {
    supplier: b.model_type === 'tgx_hotel' ? 'TravelgateX' : 'GTA',
    hotel: {
      name: b.booking_hotel ? b.booking_hotel.name : null,
      code: stay.hotel_code || null,
      address: stay.hotel_address || null,
      room_type: stay.room_type || null,
      board_type: stay.board_type || null
    },
    stay: { check_in: checkIn, check_out: checkOut, nights },
    references: { locator: stay.locator || b.hotel_locator || null, supplier_reference: stay.tgx_reference || null },
    base_price: stay.base_price,
    cancel_policy: parsePolicy(stay.cancel_policy),
    customer: null,
    lead_guest: b.booking_payment ? b.booking_payment.card_holder_name : null,
    refund: null
  }
}

export default {
  components: { BookingSummary, CustomerPanel, PaymentPanel, ReferencesPanel, StayStrip, TravellersPanel },
  mixins: [bookingPage],
  data () {
    return {
      btnLoad: false,
      disableButtonEmail: false
    }
  },
  computed: {
    details () {
      return this.booking.hotel_details || fallbackDetails(this.booking)
    },
    isCancelled () {
      return /cancel/i.test(this.booking.status || '')
    },
    // The admin cancel endpoint talks to the old GTA supplier only; TravelgateX bookings are cancelled by the customer.
    canCancel () {
      return this.booking.model_type === GTA_TYPE && !!this.booking.hotel_locator && !this.isCancelled
    },
    nightsLabel () {
      const n = this.details.stay.nights
      if (n === null || n === undefined) return '—'
      return `${n} night${n === 1 ? '' : 's'}`
    },
    references () {
      return [
        { label: 'Booking ID', value: String(this.booking.id) },
        { label: 'Reservation locator', value: this.details.references.locator },
        { label: 'Supplier reference', value: this.details.references.supplier_reference },
        { label: 'Hotel code', value: this.details.hotel.code }
      ]
    }
  },
  methods: {
    boardLabel (code) {
      if (!code) return '—'
      return boards[String(code).toUpperCase()] ? `${boards[String(code).toUpperCase()]} (${code})` : code
    },
    confirmCancel () {
      Swal.fire({
        title: 'Cancel this reservation?',
        text: 'The reservation will be cancelled at the hotel supplier.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it',
        cancelButtonText: 'Keep it'
      }).then(result => {
        if (result.isConfirmed) this.cancelReservation()
      })
    },
    cancelReservation () {
      this.btnLoad = true
      const formData = new FormData()
      formData.append('booking_locator', this.booking.hotel_locator)
      formData.append('booking_id', this.booking.id)

      this.$http.post(cancelReservation, formData, { headers: headers(this.$cookies.get('userToken')) }).then(response => {
        this.btnLoad = false
        if (response.status === 200) {
          this.notify('success', 'Booking was cancelled')
          this.disableButtonEmail = true
          this.booking.status = 'Cancelled'
        } else {
          this.notify('error', response.body.message)
        }
      }, err => {
        this.btnLoad = false
        this.notify('error', (err.body && err.body.message) || 'The reservation could not be cancelled')
      })
    }
  }
}
</script>

